import * as React from 'react'
import { ErrorBoundary } from '@/modules/shared/components/ErrorBoundary'
import { Loader } from '@/ui'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { BottomActionsBar } from './BottomActionsBar'
import { useDirtyStore } from '@/modules/shared/stores/dirtyStore'
import { computeDiff, DiffEntry } from '@/modules/shared/utils/diff'
import {
  ConfigurationRestoreActions,
  ConfigurationRestoreOptions,
} from './ConfigurationRestoreActions'

type Props = {
  isReady?: boolean
  isPending?: boolean
  isError?: boolean
  actions?: React.ReactNode
  restore?: ConfigurationRestoreOptions
  handleSave?: () => void
  handleReset?: () => void
  isDirty?: boolean
  hasValidationError?: boolean
  errorMsg?: string
  globalErrorMsg?: string | React.ReactNode
  children?: React.ReactNode
  header?: React.ReactNode
  diffBefore?: Record<string, unknown>
  diffAfter?: Record<string, unknown>
  diffLabels?: Record<string, string>
  diffFormatters?: Record<string, (value: unknown) => string>
  diffRenderers?: Record<
    string,
    (
      before: unknown,
      after: unknown,
    ) => { before: React.ReactNode; after: React.ReactNode }
  >
}

export const SettingsPageWrapper = ({
  isReady,
  isPending,
  isError,
  actions,
  restore,
  handleReset,
  handleSave,
  isDirty = false,
  hasValidationError = false,
  errorMsg,
  globalErrorMsg,
  children,
  header,
  diffBefore,
  diffAfter,
  diffLabels,
  diffFormatters,
  diffRenderers,
}: Props) => {
  const wrapConfirm = useWrapConfirm()
  const { t } = useLocalize()
  const setDirty = useDirtyStore((s) => s.setDirty)

  const diffEntries = React.useMemo<DiffEntry[]>(() => {
    if (!isDirty || !diffBefore || !diffAfter) return []
    return computeDiff(diffBefore, diffAfter, diffLabels, diffFormatters, diffRenderers)
  }, [isDirty, diffBefore, diffAfter, diffLabels, diffFormatters, diffRenderers])

  // Sync to global store so DirtyGuard and SubNavigationItem both react correctly.
  React.useEffect(() => {
    setDirty(isDirty, diffEntries)
    return () => setDirty(false)
  }, [setDirty, isDirty, diffEntries])

  void wrapConfirm
  void t

  return (
    <ErrorBoundary fallback={<>Error</> || globalErrorMsg}>
      <div className="tw-relative tw-min-h-full tw-flex tw-flex-col tw-h-full" data-test-id="settings-page-wrapper">
        {isPending ? (
          <Loader
            spinnerClassName="tw-top-0"
            shadow
            className="tw-absolute tw-z-20 tw-min-h-full tw-w-full tw-p-0"
          />
        ) : null}

        {isError ? (
          <div className="tw-p-4 tw-text-center tw-text-red-600">
            {errorMsg || 'An error occurred. Please try again later.'}
          </div>
        ) : null}

        {header}

        {isReady ? <div className='tw-flex tw-flex-1 tw-overflow-y-auto tw-flex-col'>{children}</div> : null}

        {(handleSave || handleReset || actions || restore) && <BottomActionsBar
          isReady={isReady}
          isPending={isPending}
          handleReset={handleReset}
          handleSave={handleSave}
          isDirty={isDirty}
          hasValidationError={hasValidationError}
        >
          {restore && <ConfigurationRestoreActions options={restore} isDirty={isDirty} />}
          {actions}
        </BottomActionsBar>}
      </div>
    </ErrorBoundary>
  )
}
